import { useEffect, useState } from "react";
import { Row, Col, Card, Tag, Button, Typography, Empty, Pagination } from "antd";
import { BankOutlined, ToolOutlined, TeamOutlined, FolderOpenOutlined, UnorderedListOutlined } from "@ant-design/icons";

const PAGE_SIZE = 8;

const { Text, Paragraph } = Typography;

const NAVY = "#042956";

// Mapea el estado a un color de tag institucional.
function estadoColor(estado = "") {
  const e = estado.toLowerCase();
  if (e.includes("final") || e.includes("cerrad") || e.includes("complet")) return "green";
  if (e.includes("ejecu") || e.includes("proceso") || e.includes("curso")) return "blue";
  if (e.includes("pend") || e.includes("espera")) return "gold";
  if (e.includes("suspend") || e.includes("cancel")) return "red";
  return "default";
}

function InfoRow({ icon, label, value, color }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10}}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: `${color}16`,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          flexShrink: 0
        }}
      >
        {icon}
      </div>
      <Text style={{ color: "#8c9bab", fontSize: 12.5, minWidth: 88 }}>{label}</Text>
      <Text
        ellipsis
        style={{ color: "#37475a", fontSize: 13, fontWeight: 600, flex: 1, textAlign: "right" }}
        title={value}
      >
        {value || "—"}
      </Text>
    </div>
  );
}

function ProjectCard({ proyecto, onSelect }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Card
      onClick={() => onSelect?.(proyecto)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      styles={{ body: { padding: 22, display: "flex", flexDirection: "column", height: "100%", } }}
      style={{
        borderRadius: 16,
        padding: 10,
        border: `1px solid ${hovered ? NAVY : "#eef1f5"}`,
        boxShadow: hovered ? "0 10px 24px rgba(4, 41, 86, 0.14)" : "0 1px 4px rgba(4, 41, 86, 0.06)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        transition: "all 0.25s ease",
        height: "100%",
        cursor: "pointer"
      }}
    >
      {/* Encabezado: código + estado */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <span
          style={{
            background: NAVY,
            color: "#ffffff",
            fontWeight: 700,
            fontSize: 13,
            padding: "4px 12px",
            borderRadius: 8,
            letterSpacing: 0.5
          }}
        >
          {proyecto.codigo}
        </span>
        <Tag color={estadoColor(proyecto.ac_estado)} style={{ margin: 0, borderRadius: 6, fontWeight: 500 }}>
          {proyecto.ac_estado || "Sin estado"}
        </Tag>
      </div>

      {/* Título del proyecto */}
      <Paragraph
        ellipsis={{ rows: 2 }}
        style={{ color: NAVY, fontSize: 15, fontWeight: 600, lineHeight: 1.4, marginBottom: 6, minHeight: 42 }}
        title={proyecto.proyecto}
      >
        {proyecto.proyecto}
      </Paragraph>

      {/* Subtítulo: proyecto asociado */}
      <Text
        ellipsis
        style={{ color: "#8c9bab", fontSize: 12.5, marginBottom: 18, display: "block", lineHeight: 1.6 }}
        title={proyecto.Nombre_proyectoAsociado}
      >
        {proyecto.Nombre_proyectoAsociado}
      </Text>

      {/* Metadatos */}
      <div
        style={{
          borderTop: "1px solid #f2f5f9",
          paddingTop: 16,
          marginTop: "auto",
          marginBottom: 18,
          display: "flex",
          flexDirection: "column",
          gap: 12
        }}
      >
        <InfoRow icon={<BankOutlined />} label="Implementador" value={proyecto.implementador} color="#1677ff" />
        <InfoRow icon={<ToolOutlined />} label="Operador" value={proyecto.operador} color="#13c2c2" />
        <InfoRow icon={<TeamOutlined />} label="Beneficiario" value={proyecto.tipo_beneficiario} color="#722ed1" />
      </div>

      {/* Acción */}
      <Button
        type="primary"
        block
        icon={<FolderOpenOutlined />}
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.(proyecto);
        }}
        style={{
          background: NAVY,
          borderColor: NAVY,
          borderRadius: 10,
          height: 42,
          fontWeight: 500,
          boxShadow: hovered ? "0 4px 10px rgba(4, 41, 86, 0.25)" : "none",
          transition: "all 0.25s ease"
        }}
      >
        Ver documentos
      </Button>
    </Card>
  );
}

/**
 * ProyectosCards
 * Props:
 *  - proyectos: Array<{ oid, codigo, proyecto, ac_estado, codigo_ProyectoArt,
 *      Nombre_proyectoAsociado, implementador, operador, tipo_beneficiario }>
 *  - onSelect(proyecto)
 */
export default function ProyectosCards({ proyectos = [], onSelect }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);

  // Si cambian los filtros (nuevo array), vuelve siempre a la primera página.
  useEffect(() => {
    setCurrentPage(1);
  }, [proyectos]);

  if (!proyectos.length) {
    return (
      <div
        style={{
          background: "#ffffff",
          borderRadius: 16,
          padding: 48,
          border: "1px solid #eef1f5"
        }}
      >
        <Empty description="No se encontraron proyectos con los filtros aplicados" />
      </div>
    );
  }

  const necesitaPaginar = proyectos.length > PAGE_SIZE;
  const visibles = showAll
    ? proyectos
    : proyectos.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <>
      <Row gutter={[20, 20]}>
        {visibles.map((proyecto) => (
          <Col key={proyecto.oid} xs={24} sm={12} lg={8} xl={6}>
            <ProjectCard proyecto={proyecto} onSelect={onSelect} />
          </Col>
        ))}
      </Row>

      {necesitaPaginar && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
            marginTop: 28
          }}
        >
          {!showAll && (
            <Pagination
              current={currentPage}
              pageSize={PAGE_SIZE}
              total={proyectos.length}
              onChange={setCurrentPage}
              showTotal={(total, range) => `${range[0]}-${range[1]} de ${total} proyectos`}
            />
          )}
          <Button
            type="text"
            icon={<UnorderedListOutlined />}
            onClick={() => setShowAll((v) => !v)}
            style={{ color: "#042956", fontWeight: 500 }}
          >
            {showAll ? "Volver a paginar" : `Ver los ${proyectos.length} proyectos`}
          </Button>
        </div>
      )}
    </>
  );
}
